import { useRef, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import type { Album, Artist, Composer, Playlist } from '@pujolisique/shared';
import { fetchAlbum, fetchPlaylist } from '../api/hooks';
import { usePlayer } from '../player/store';
import { toast } from '../ui/toast';
import { plural } from '../lib/format';
import { Cover } from './Cover';
import { useAlbumMenu, useStartMix } from './trackActions';

export function Shelf({ title, to, children, sub }: { title: string; to?: string; sub?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: number) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.85, behavior: 'smooth' });
  return (
    <section className="shelf">
      <header className="shelf__head">
        <div>
          <h2 className="shelf__title">{to ? <Link to={to}>{title}</Link> : title}</h2>
          {sub && <p className="shelf__sub">{sub}</p>}
        </div>
        <div className="shelf__nav">
          {to && (
            <Link className="link-quiet" to={to}>
              Tout afficher
            </Link>
          )}
          <button className="icon-btn icon-btn--ring" onClick={() => scroll(-1)} aria-label="Défiler vers la gauche">
            <ChevronLeft size={18} />
          </button>
          <button className="icon-btn icon-btn--ring" onClick={() => scroll(1)} aria-label="Défiler vers la droite">
            <ChevronRight size={18} />
          </button>
        </div>
      </header>
      <div className="shelf__row" ref={ref}>
        {children}
      </div>
    </section>
  );
}

function PlayFab({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      className="fab"
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <Play size={20} fill="currentColor" />
    </button>
  );
}

export function AlbumCard({ album, sub }: { album: Album; sub?: string }) {
  const playTracks = usePlayer((s) => s.playTracks);
  const menu = useAlbumMenu();
  const play = async () => {
    try {
      const a = await fetchAlbum(album.id);
      playTracks(a.tracks, 0, { source: a.name });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  return (
    <Link
      to={`/album/${album.id}`}
      className="card"
      onContextMenu={(e) => {
        e.preventDefault();
        menu(album, e);
      }}
    >
      <div className="card__art">
        <Cover art={`album/${album.id}`} name={album.name} size={256} />
        <PlayFab onClick={play} label={`Lire ${album.name}`} />
      </div>
      <span className="card__title">{album.name}</span>
      <span className="card__sub">{sub ?? [album.artists.map((a) => a.name).join(', '), album.year].filter(Boolean).join(', ')}</span>
    </Link>
  );
}

export function PersonCard({ person, kind }: { person: Artist | Composer; kind: 'artist' | 'composer' }) {
  const mix = useStartMix();
  const count =
    kind === 'composer'
      ? (person as Composer).trackCount !== undefined
        ? plural((person as Composer).trackCount!, 'œuvre', 'œuvres')
        : 'Compositeur'
      : person.albumCount
        ? plural(person.albumCount, 'album', 'albums')
        : 'Artiste';
  return (
    <Link to={`/${kind}/${person.id}`} className="card card--person">
      <div className="card__art">
        <Cover art={`${kind}/${person.id}`} name={person.name} size={256} round />
        <PlayFab onClick={() => mix(kind, person.id, person.name)} label={`Lancer un mix ${person.name}`} />
      </div>
      <span className="card__title">{person.name}</span>
      <span className="card__sub">{count}</span>
    </Link>
  );
}

export function PlaylistCard({ playlist }: { playlist: Playlist }) {
  const playTracks = usePlayer((s) => s.playTracks);
  const nav = useNavigate();
  return (
    <Link to={`/playlist/${playlist.id}`} className="card">
      <div className="card__art">
        <Cover art={`playlist/${playlist.id}`} name={playlist.name} size={256} />
        <PlayFab
          label={`Lire ${playlist.name}`}
          onClick={async () => {
            const p = await fetchPlaylist(playlist.id).catch(() => null);
            if (p?.tracks.length) playTracks(p.tracks, 0, { source: p.name });
            else nav(`/playlist/${playlist.id}`);
          }}
        />
      </div>
      <span className="card__title">{playlist.name}</span>
      <span className="card__sub">{plural(playlist.trackCount, 'titre', 'titres')}</span>
    </Link>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid">{children}</div>;
}
